import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaBell,
  FaCheckCircle,
  FaEdit,
  FaExclamationCircle,
  FaFilter,
  FaMoneyBillWave,
  FaPlus,
  FaReceipt,
  FaSearch,
  FaTrash,
  FaUniversity
} from 'react-icons/fa';
import { invoiceAPI, paymentAPI } from '../services/api';
import Modal from '../components/Modal';
import './PaymentsPage.css';

const currency = (amount) => `Rs ${Number(amount || 0).toFixed(2)}`;

const buildPaymentSummary = (rows = []) =>
  rows.reduce(
    (acc, payment) => {
      acc.totalPayments += 1;
      acc.totalReceived += Number(payment.AmountPaid || 0);
      acc.methods[payment.PaymentMethod || 'Unknown'] = (acc.methods[payment.PaymentMethod || 'Unknown'] || 0) + 1;
      acc.invoiceIds.add(payment.InvoiceID);
      return acc;
    },
    {
      totalPayments: 0,
      totalReceived: 0,
      methods: {},
      invoiceIds: new Set()
    }
  );

const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existingScript = document.querySelector('script[data-razorpay-checkout="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(true));
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Razorpay checkout')));
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.dataset.razorpayCheckout = 'true';
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('Failed to load Razorpay checkout'));
    document.body.appendChild(script);
  });

function PaymentsPage({
  studentId = null,
  readOnly = false,
  title = 'Payment Management',
  subtitle = 'Track received amounts, methods used, invoice balances, and payment history.'
}) {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [gatewayConfig, setGatewayConfig] = useState({ enabled: false, key: '' });
  const [pendingFeeReport, setPendingFeeReport] = useState({ rows: [], totalPendingAmount: 0, unpaidStudents: 0, totalPendingInvoices: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPendingOnly, setShowPendingOnly] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [processingInvoiceId, setProcessingInvoiceId] = useState(null);
  const [remindingInvoiceId, setRemindingInvoiceId] = useState(null);
  const [successPopup, setSuccessPopup] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    InvoiceID: '',
    PaymentDate: new Date().toISOString().slice(0, 16),
    AmountPaid: '',
    PaymentMethod: 'Cash'
  });

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const invoiceRequest = readOnly && studentId ? invoiceAPI.getByStudent(studentId) : invoiceAPI.getAll();
      const requests = [paymentAPI.getAll(), invoiceRequest, paymentAPI.getGatewayConfig()];
      if (!readOnly) {
        requests.push(paymentAPI.getPendingFees({ search: searchTerm }));
      }

      const responses = await Promise.all(requests);
      const paymentRows = responses[0].data || [];
      const invoiceRows = responses[1].data || [];
      const gateway = responses[2].data || { enabled: false, key: '' };
      const pendingReport = !readOnly ? responses[3]?.data || { rows: [] } : { rows: [] };

      setPayments(paymentRows);
      setInvoices(invoiceRows);
      setGatewayConfig(gateway);
      setPendingFeeReport(pendingReport);

      try {
        const summaryResponse = await paymentAPI.getSummary(readOnly && studentId ? { studentId } : undefined);
        setSummary(summaryResponse.data || null);
      } catch (summaryError) {
        if (summaryError?.response?.status === 404) {
          const localSummary = buildPaymentSummary(paymentRows);
          setSummary({
            totalPayments: localSummary.totalPayments,
            totalReceived: localSummary.totalReceived,
            invoicesCovered: localSummary.invoiceIds.size,
            methods: localSummary.methods
          });
        } else {
          throw summaryError;
        }
      }

      setError('');
    } catch (err) {
      setError('Failed to fetch payments: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [readOnly, searchTerm, studentId]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const pendingInvoices = useMemo(
    () =>
      invoices.filter((invoice) => {
        const status = String(invoice.ComputedStatus || invoice.Status || '').toLowerCase();
        return ['pending', 'partial'].includes(status) && Number(invoice.BalanceDue || 0) > 0;
      }),
    [invoices]
  );

  const selectableInvoices = useMemo(() => {
    const invoiceMap = new Map();

    pendingInvoices.forEach((invoice) => {
      invoiceMap.set(String(invoice.InvoiceID), invoice);
    });

    if (editingId && formData.InvoiceID) {
      const currentInvoice = invoices.find((invoice) => String(invoice.InvoiceID) === String(formData.InvoiceID));
      if (currentInvoice) {
        invoiceMap.set(String(currentInvoice.InvoiceID), currentInvoice);
      }
    }

    return Array.from(invoiceMap.values());
  }, [editingId, formData.InvoiceID, invoices, pendingInvoices]);

  const selectedInvoice = useMemo(
    () => invoices.find((invoice) => String(invoice.InvoiceID) === String(formData.InvoiceID)),
    [formData.InvoiceID, invoices]
  );

  const getInvoiceInfo = useCallback(
    (invoiceId) => invoices.find((invoice) => String(invoice.InvoiceID) === String(invoiceId)),
    [invoices]
  );

  const getPaymentInvoiceStatus = useCallback(
    (payment) => String(getInvoiceInfo(payment?.InvoiceID)?.ComputedStatus || payment?.InvoiceStatus || 'Pending').toLowerCase(),
    [getInvoiceInfo]
  );

  const getLatestEditableCashPayment = useCallback(
    (invoiceId) => {
      const matchingPayments = payments
        .filter((payment) =>
          String(payment?.InvoiceID) === String(invoiceId)
          && String(payment?.PaymentMethod || '').toLowerCase() === 'cash'
          && ['manual', ''].includes(String(payment?.GatewayProvider || '').toLowerCase())
        )
        .sort((a, b) => {
          const left = new Date(a?.PaymentDate || 0).getTime();
          const right = new Date(b?.PaymentDate || 0).getTime();
          return right - left;
        });

      return matchingPayments[0] || null;
    },
    [payments]
  );

  const invoiceMetrics = useMemo(() => {
    const totals = invoices.reduce(
      (acc, invoice) => {
        const status = String(invoice.ComputedStatus || invoice.Status || 'Pending').toLowerCase();
        const total = Number(invoice.TotalAmount || 0);
        const paid = Number(invoice.AmountPaid || 0);
        const balance = Number(invoice.BalanceDue || 0);

        acc.totalBilled += total;
        acc.totalCollected += paid;
        acc.totalOutstanding += balance;
        acc.statusCounts[status] = (acc.statusCounts[status] || 0) + 1;

        if (balance > 0) {
          acc.unpaidInvoices += 1;
        }

        return acc;
      },
      {
        totalBilled: 0,
        totalCollected: 0,
        totalOutstanding: 0,
        unpaidInvoices: 0,
        statusCounts: { paid: 0, partial: 0, pending: 0, cancelled: 0 }
      }
    );

    return totals;
  }, [invoices]);

  const paymentMethods = useMemo(
    () => Array.from(new Set(payments.map((payment) => payment.PaymentMethod).filter(Boolean))),
    [payments]
  );

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: 'All Rows' },
      { value: 'unpaid', label: 'Unpaid Invoices' },
      { value: 'paid', label: 'Paid Invoices' },
      { value: 'partial', label: 'Partial Invoices' },
      { value: 'pending', label: 'Pending Invoices' },
      { value: 'cancelled', label: 'Cancelled Invoices' }
    ],
    []
  );

  const filteredPayments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const visiblePayments = payments
      .filter((payment) => {
        if (showPendingOnly && !readOnly && Number(payment.InvoiceBalance || 0) <= 0) {
          return false;
        }

        const invoiceStatus = getPaymentInvoiceStatus(payment);
        if (statusFilter === 'unpaid') {
          if (Number(payment.InvoiceBalance || 0) <= 0) return false;
        } else if (statusFilter !== 'all' && invoiceStatus !== statusFilter) {
          return false;
        }

        if (methodFilter !== 'all' && String(payment.PaymentMethod || '').toLowerCase() !== methodFilter) {
          return false;
        }

        return true;
      });

    if (!term) {
      return visiblePayments;
    }

    return visiblePayments.filter((payment) => {
      const method = String(payment.PaymentMethod || '').toLowerCase();
      const student = String(payment.StudentName || '').toLowerCase();
      const invoiceId = String(payment.InvoiceID || '').toLowerCase();
      const amount = String(payment.AmountPaid || '').toLowerCase();
      const invoiceStatus = getPaymentInvoiceStatus(payment);
      return method.includes(term) || student.includes(term) || invoiceId.includes(term) || amount.includes(term) || invoiceStatus.includes(term);
    });
  }, [getPaymentInvoiceStatus, methodFilter, payments, readOnly, searchTerm, showPendingOnly, statusFilter]);

  const summaryCards = [
    { label: 'Payments Recorded', value: summary?.totalPayments || payments.length, icon: <FaReceipt /> },
    { label: 'Total Received', value: currency(summary?.totalReceived), icon: <FaMoneyBillWave /> },
    { label: readOnly ? 'Pending Invoices' : 'Pending Fee Invoices', value: readOnly ? pendingInvoices.length : pendingFeeReport.totalPendingInvoices || 0, icon: <FaExclamationCircle /> },
    { label: 'Top Methods', value: Object.keys(summary?.methods || {}).length || 0, icon: <FaUniversity /> }
  ];

  const amountCards = [
    { label: 'Total Billed', value: currency(invoiceMetrics.totalBilled) },
    { label: 'Collected Amount', value: currency(invoiceMetrics.totalCollected) },
    { label: 'Outstanding Amount', value: currency(invoiceMetrics.totalOutstanding) },
    { label: 'Paid Invoices', value: invoiceMetrics.statusCounts.paid || 0 },
    { label: 'Partial Invoices', value: invoiceMetrics.statusCounts.partial || 0 },
    { label: 'Pending Invoices', value: invoiceMetrics.statusCounts.pending || 0 }
  ];

  const handleOpenModal = (payment = null) => {
    if (payment) {
      setEditingId(payment.PaymentID);
      const formattedDate = payment.PaymentDate ? new Date(payment.PaymentDate).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16);
      setFormData({
        InvoiceID: payment.InvoiceID || '',
        PaymentDate: formattedDate,
        AmountPaid: payment.AmountPaid || '',
        PaymentMethod: payment.PaymentMethod || 'Cash'
      });
    } else {
      setEditingId(null);
      setFormData({
        InvoiceID: '',
        PaymentDate: new Date().toISOString().slice(0, 16),
        AmountPaid: '',
        PaymentMethod: 'Cash'
      });
    }
    setIsModalOpen(true);
  };

  const handleRecordInvoicePayment = (invoice) => {
    setEditingId(null);
    setFormData({
      InvoiceID: invoice?.InvoiceID || '',
      PaymentDate: new Date().toISOString().slice(0, 16),
      AmountPaid: Number(invoice?.BalanceDue || 0) > 0 ? String(Number(invoice.BalanceDue || 0)) : '',
      PaymentMethod: 'Cash'
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingId && !selectableInvoices.some((invoice) => String(invoice.InvoiceID) === String(formData.InvoiceID))) {
      setError('Please select an unpaid or pending invoice.');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await paymentAPI.update(editingId, formData);
      } else {
        await paymentAPI.create(formData);
      }
      await fetchPayments();
      handleCloseModal();
      setError('');
      setMessage('Payment saved successfully.');
    } catch (err) {
      setError('Failed to save payment: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      try {
        await paymentAPI.delete(id);
        await fetchPayments();
        setError('');
        setMessage('Payment deleted successfully.');
      } catch (err) {
        setError('Failed to delete payment: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const handleSendReminder = async (invoiceOrId, channels = ['email']) => {
    const invoiceId = typeof invoiceOrId === 'object' ? invoiceOrId.InvoiceID : invoiceOrId;
    const inv = typeof invoiceOrId === 'object' ? invoiceOrId : getInvoiceInfo(invoiceId);
    try {
      setRemindingInvoiceId(invoiceId);
      setError('');
      setMessage('');
      const res = await paymentAPI.sendInvoiceReminder(invoiceId, { channels });
      const successMsg = res?.data?.message || 'Email reminder queued successfully.';
      setMessage(successMsg);
      setSuccessPopup({
        title: 'Email Reminder Sent!',
        message: successMsg,
        studentName: inv?.StudentName || (inv?.StudentID ? `Student ${inv.StudentID}` : 'N/A'),
        invoiceId: invoiceId,
        balance: inv?.BalanceDue,
        contactNumber: inv?.Email || 'N/A'
      });
      await fetchPayments();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to send reminder');
    } finally {
      setRemindingInvoiceId(null);
    }
  };

  const handleSendWhatsAppReminder = async (invoice) => {
    const studentId = invoice?.StudentID || invoice?.studentId || invoice?.UserID || invoice?.userId || getInvoiceInfo(invoice?.InvoiceID)?.StudentID;
    if (!studentId) {
      setError('Student ID not found for this invoice.');
      return;
    }

    try {
      setRemindingInvoiceId(invoice.InvoiceID);
      setError('');
      setMessage('');

      const response = await invoiceAPI.sendWhatsAppReminder(studentId, {
        invoiceId: invoice.InvoiceID
      });

      if (invoice.Email) {
        try {
          await paymentAPI.sendInvoiceReminder(invoice.InvoiceID, { channels: ['email'] });
        } catch (emailErr) {
          console.warn('Optional email reminder skipped or failed:', emailErr);
        }
      }

      const successMsg = response?.data?.message || 'WhatsApp reminder sent successfully!';
      setMessage(successMsg);
      setSuccessPopup({
        title: 'WhatsApp Reminder Sent!',
        message: successMsg,
        studentName: invoice?.StudentName || `Student ${studentId}`,
        invoiceId: invoice?.InvoiceID,
        balance: invoice?.BalanceDue,
        contactNumber: invoice?.ContactNumber || 'N/A'
      });
      await fetchPayments();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to send WhatsApp reminder');
    } finally {
      setRemindingInvoiceId(null);
    }
  };

  const handlePayNow = async (invoice) => {
    try {
      setProcessingInvoiceId(invoice.InvoiceID);
      setError('');
      setMessage('');
      const [orderResponse] = await Promise.all([
        paymentAPI.createCheckoutOrder({ invoiceId: invoice.InvoiceID }),
        loadRazorpayScript()
      ]);

      const order = orderResponse.data;
      const razorpay = new window.Razorpay({
        key: order.razorpayKey,
        order_id: order.orderId,
        amount: order.orderAmount || Math.round(Number(order.amount || 0) * 100),
        currency: order.currency || 'INR',
        name: 'EPSILON SKATING CLUB Club',
        description: order.description || `Invoice #${invoice.InvoiceID}`,
        handler: async (response) => {
          try {
            await paymentAPI.verifyCheckoutPayment({
              invoiceId: invoice.InvoiceID,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            setMessage('Payment completed successfully.');
            await fetchPayments();
          } catch (verificationError) {
            setError(verificationError.response?.data?.error || verificationError.message || 'Payment verification failed');
          } finally {
            setProcessingInvoiceId(null);
          }
        },
        prefill: {
          name: invoice.StudentName || '',
          email: invoice.Email || '',
          contact: invoice.ContactNumber || ''
        },
        theme: {
          color: '#2563eb'
        },
        modal: {
          ondismiss: () => {
            setProcessingInvoiceId(null);
          }
        }
      });

      razorpay.open();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to start payment');
      setProcessingInvoiceId(null);
    }
  };

  const filteredInvoiceStatuses = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return invoices.filter((invoice) => {
      const status = String(invoice.ComputedStatus || invoice.Status || 'Pending').toLowerCase();
      const student = String(invoice.StudentName || invoice.StudentID || '').toLowerCase();
      const invoiceId = String(invoice.InvoiceID || '').toLowerCase();
      const description = String(invoice.Description || invoice.SourceType || '').toLowerCase();
      const dueDate = String(invoice.DueDate || '').toLowerCase();

      if (statusFilter === 'unpaid' && Number(invoice.BalanceDue || 0) <= 0) {
        return false;
      }

      if (statusFilter !== 'all' && statusFilter !== 'unpaid' && status !== statusFilter) {
        return false;
      }

      if (!term) {
        return true;
      }

      return invoiceId.includes(term)
        || student.includes(term)
        || description.includes(term)
        || dueDate.includes(term)
        || status.includes(term);
    });
  }, [invoices, searchTerm, statusFilter]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className="payments-page admin-module-shell">
      <div className="module-panel">
        <div className="page-header">
          <div>
            <h1 className="page-title">{title}</h1>
            <p className="page-subtitle">{subtitle}</p>
          </div>
          {!readOnly && (
            <button className="btn btn-primary" onClick={() => handleOpenModal()} disabled={selectableInvoices.length === 0}>
              <FaPlus /> Record Payment
            </button>
          )}
        </div>

        {!readOnly && selectableInvoices.length === 0 && (
          <div className="alert alert-success">All invoices are already paid right now.</div>
        )}

        <div className="summary-grid">
          {summaryCards.map((card) => (
            <article className="summary-card" key={card.label}>
              <span className="summary-icon">{card.icon}</span>
              <p>{card.label}</p>
              <h3>{card.value}</h3>
            </article>
          ))}
        </div>

        {!readOnly && (
          <section className="amount-overview-grid">
            {amountCards.map((card) => (
              <article className="amount-overview-card" key={card.label}>
                <span>{card.label}</span>
                <strong>{card.value}</strong>
              </article>
            ))}
          </section>
        )}

        <div className="method-grid">
          {Object.entries(summary?.methods || {}).map(([method, count]) => (
            <div className="method-card" key={method}>
              <span>{method}</span>
              <strong>{count}</strong>
            </div>
          ))}
        </div>

        {!readOnly && (
          <div className="pending-summary-strip">
            <article>
              <span>Unpaid Students</span>
              <strong>{pendingFeeReport.unpaidStudents || 0}</strong>
            </article>
            <article>
              <span>Pending Amount</span>
              <strong>{currency(pendingFeeReport.totalPendingAmount)}</strong>
            </article>
            <label className="pending-toggle">
              <input
                type="checkbox"
                checked={showPendingOnly}
                onChange={(e) => setShowPendingOnly(e.target.checked)}
              />
              Show only unpaid / partial payment rows
            </label>
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}

        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder={readOnly ? 'Search by invoice or payment method...' : 'Search unpaid students, invoices, email, phone, or methods...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {!readOnly && (
          <section className="payment-filter-bar">
            <div className="payment-filter-group">
              <label><FaFilter /> Invoice Status</label>
              <div className="payment-filter-chips">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`payment-filter-chip ${statusFilter === option.value ? 'active' : ''}`}
                    onClick={() => setStatusFilter(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="payment-filter-select">
              <label>Payment Method</label>
              <select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)}>
                <option value="all">All Methods</option>
                {paymentMethods.map((method) => (
                  <option key={method} value={String(method).toLowerCase()}>
                    {method}
                  </option>
                ))}
              </select>
            </div>
          </section>
        )}

        {!readOnly && (
          <section className="invoice-status-section">
            <div className="pending-section-header">
              <h2>Invoice Status</h2>
              <p>View all invoices with billed amount, collected amount, remaining balance, due date, and current status.</p>
            </div>

            {filteredInvoiceStatuses.length === 0 ? (
              <div className="no-data">No invoices match the selected status or search.</div>
            ) : (
              <div className="table-responsive">
                <table className="responsive-table invoice-status-table">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Student</th>
                      <th>Billed</th>
                      <th>Collected</th>
                      <th>Balance</th>
                      <th>Due Date</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoiceStatuses.map((invoice) => {
                      const invoiceStatus = String(invoice.ComputedStatus || invoice.Status || 'Pending').toLowerCase();
                      const canRecordPayment = Number(invoice.BalanceDue || 0) > 0 && invoiceStatus !== 'cancelled';
                      const editableCashPayment = getLatestEditableCashPayment(invoice.InvoiceID);

                      return (
                        <tr key={invoice.InvoiceID}>
                          <td data-label="Invoice">
                            <strong>#{invoice.InvoiceID}</strong>
                            <div>{invoice.Description || invoice.SourceType || 'General invoice'}</div>
                          </td>
                          <td data-label="Student">{invoice.StudentName || invoice.StudentID || 'N/A'}</td>
                          <td data-label="Billed">{currency(invoice.TotalAmount)}</td>
                          <td data-label="Collected">{currency(invoice.AmountPaid)}</td>
                          <td data-label="Balance">{currency(invoice.BalanceDue)}</td>
                          <td data-label="Due Date">{formatDate(invoice.DueDate)}</td>
                          <td data-label="Status">
                            <span className={`status-badge ${invoiceStatus}`}>
                              {invoice.ComputedStatus || invoice.Status || 'Pending'}
                            </span>
                          </td>
                          <td data-label="Action" className="action-buttons">
                            {canRecordPayment && (
                              <button
                                type="button"
                                className="btn btn-small btn-primary"
                                onClick={() => handleRecordInvoicePayment(invoice)}
                              >
                                Record Payment
                              </button>
                            )}
                            {editableCashPayment && (
                              <button
                                type="button"
                                className="btn btn-small btn-secondary"
                                onClick={() => handleOpenModal(editableCashPayment)}
                              >
                                Update Cash Payment
                              </button>
                            )}
                            {!canRecordPayment && !editableCashPayment && (
                              <span className="status-note">No action needed</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {(readOnly ? pendingInvoices.length > 0 : pendingFeeReport.rows.length > 0) && (
          <section className="pending-fee-section">
            <div className="pending-section-header">
              <h2>{readOnly ? 'Pending Fee Payments' : 'Pending Fee Tracker'}</h2>
              {!readOnly && <p>Send reminders and track students who still need to pay product or batch fees.</p>}
            </div>

            <div className="pending-fee-grid">
              {(readOnly ? pendingInvoices : pendingFeeReport.rows).map((invoice) => (
                <article key={invoice.InvoiceID} className="pending-fee-card">
                  <div className="pending-fee-top">
                    <div>
                      <strong>{invoice.StudentName || `Student ${invoice.StudentID}`}</strong>
                      <p>{`Invoice #${invoice.InvoiceID}`}</p>
                    </div>
                    <span className={`status-badge ${String(invoice.ComputedStatus || invoice.Status || 'Pending').toLowerCase()}`}>
                      {invoice.ComputedStatus || invoice.Status || 'Pending'}
                    </span>
                  </div>

                  <div className="pending-fee-body">
                    <p>{invoice.Description || `${invoice.SourceType || 'Fee'} payment pending`}</p>
                    <div className="pending-fee-metrics">
                      <span>Due: {invoice.DueDate ? String(invoice.DueDate).split('T')[0] : 'N/A'}</span>
                      <span>Balance: {currency(invoice.BalanceDue)}</span>
                      {!readOnly && <span>Reminders: {invoice.ReminderCount || 0}</span>}
                    </div>
                    {!readOnly && (
                      <div className="pending-fee-contact">
                        <span>{invoice.Email || 'No email'}</span>
                        <span>{invoice.ContactNumber || 'No WhatsApp number'}</span>
                      </div>
                    )}
                  </div>

                  <div className="pending-fee-actions">
                    {readOnly ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => handlePayNow(invoice)}
                        disabled={processingInvoiceId === invoice.InvoiceID || !gatewayConfig.enabled}
                      >
                        {processingInvoiceId === invoice.InvoiceID ? 'Starting payment...' : gatewayConfig.enabled ? 'Pay With Razorpay' : 'Razorpay Not Configured'}
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => handleSendReminder(invoice, ['email'])}
                          disabled={remindingInvoiceId === invoice.InvoiceID}
                        >
                          {remindingInvoiceId === invoice.InvoiceID ? 'Sending...' : (
                            <>
                              <FaBell /> Email Reminder
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => handleSendWhatsAppReminder(invoice)}
                          disabled={remindingInvoiceId === invoice.InvoiceID}
                        >
                          {remindingInvoiceId === invoice.InvoiceID ? 'Sending...' : (
                            <>
                              <FaBell /> Email + WhatsApp
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {loading ? (
          <div className="loading">Loading payments...</div>
        ) : filteredPayments.length === 0 ? (
          <div className="no-data">No payments found</div>
        ) : (
          <div className="table-responsive">
            <table className="responsive-table">
              <thead>
                <tr>
                  <th>Payment ID</th>
                  <th>Invoice</th>
                  <th>Student</th>
                  <th>Amount</th>
                  <th>Invoice Total</th>
                  <th>Total Collected</th>
                  <th>Date</th>
                  <th>Method</th>
                  {!readOnly && <th>Invoice Status</th>}
                  <th>Balance After</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((payment) => (
                  <tr key={payment.PaymentID}>
                    <td data-label="Payment ID">#{payment.PaymentID}</td>
                    <td data-label="Invoice">#{payment.InvoiceID}</td>
                    <td data-label="Student">{payment.StudentName || 'N/A'}</td>
                    <td data-label="Amount">{currency(payment.AmountPaid)}</td>
                    <td data-label="Invoice Total">{currency(payment.InvoiceTotal)}</td>
                    <td data-label="Total Collected">{currency(payment.InvoiceAmountPaid)}</td>
                    <td data-label="Date">{formatDate(payment.PaymentDate)}</td>
                    <td data-label="Method">{payment.PaymentMethod || 'N/A'}</td>
                    {!readOnly && (
                      <td data-label="Invoice Status">
                        <span className={`status-badge ${getPaymentInvoiceStatus(payment)}`}>
                          {getInvoiceInfo(payment.InvoiceID)?.ComputedStatus || payment.InvoiceStatus || 'Pending'}
                        </span>
                      </td>
                    )}
                    <td data-label="Balance After">{currency(payment.InvoiceBalance)}</td>
                    <td data-label="Actions" className="action-buttons">
                      <button className="btn btn-small btn-secondary" onClick={() => setSelectedPayment(payment)}>
                        Details
                      </button>
                      {!readOnly && (
                        <>
                          <button className="btn btn-small btn-primary" onClick={() => handleOpenModal(payment)}>
                            <FaEdit /> Edit
                          </button>
                          <button className="btn btn-small btn-danger" onClick={() => handleDelete(payment.PaymentID)}>
                            <FaTrash /> Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!readOnly && (
        <Modal isOpen={isModalOpen} title={editingId ? 'Edit Payment' : 'Record Payment'} onClose={handleCloseModal}>
          <form onSubmit={handleSubmit} className="payment-form">
            <div className="form-group">
              <label>Invoice *</label>
              <select name="InvoiceID" value={formData.InvoiceID} onChange={handleInputChange} required>
                <option value="">{selectableInvoices.length ? 'Select Unpaid Invoice' : 'No unpaid invoices available'}</option>
                {selectableInvoices.map((invoice) => (
                  <option key={invoice.InvoiceID} value={invoice.InvoiceID}>
                    {`Invoice #${invoice.InvoiceID} - ${invoice.StudentName || invoice.StudentID} - ${currency(invoice.BalanceDue || invoice.TotalAmount)}`}
                  </option>
                ))}
              </select>
              {selectedInvoice && (
                <div className="payment-inline-summary">
                  <span>{`Invoice Total: ${currency(selectedInvoice.TotalAmount)}`}</span>
                  <span>{`Already Paid: ${currency(selectedInvoice.AmountPaid)}`}</span>
                  <span>{`Pending Balance: ${currency(selectedInvoice.BalanceDue || selectedInvoice.TotalAmount)}`}</span>
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Payment Date</label>
                <input type="datetime-local" name="PaymentDate" value={formData.PaymentDate} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Amount Paid *</label>
                <input
                  type="number"
                  name="AmountPaid"
                  value={formData.AmountPaid}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0.01"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Payment Method *</label>
              <select name="PaymentMethod" value={formData.PaymentMethod} onChange={handleInputChange} required>
                <option value="Cash">Cash</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Debit Card">Debit Card</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="UPI">UPI</option>
                <option value="Check">Check</option>
              </select>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : editingId ? 'Update Payment' : 'Record Payment'}</button>
              <button type="button" className="btn btn-secondary" onClick={handleCloseModal} disabled={saving}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      <Modal isOpen={selectedPayment !== null} title={`Payment #${selectedPayment?.PaymentID || ''}`} onClose={() => setSelectedPayment(null)}>
        <div className="payment-detail-grid">
          <article>
            <label>Invoice</label>
            <p>#{selectedPayment?.InvoiceID}</p>
          </article>
          <article>
            <label>Student</label>
            <p>{selectedPayment?.StudentName || 'N/A'}</p>
          </article>
          <article>
            <label>Payment Method</label>
            <p>{selectedPayment?.PaymentMethod || 'N/A'}</p>
          </article>
          <article>
            <label>Payment Date</label>
            <p>{formatDate(selectedPayment?.PaymentDate)}</p>
          </article>
          <article>
            <label>Amount Paid</label>
            <p>{currency(selectedPayment?.AmountPaid)}</p>
          </article>
          <article>
            <label>Invoice Total</label>
            <p>{currency(selectedPayment?.InvoiceTotal)}</p>
          </article>
          <article>
            <label>Collected Before This Payment</label>
            <p>{currency(Number(selectedPayment?.InvoiceAmountPaid || 0) - Number(selectedPayment?.AmountPaid || 0))}</p>
          </article>
          <article>
            <label>Total Collected</label>
            <p>{currency(selectedPayment?.InvoiceAmountPaid)}</p>
          </article>
          <article>
            <label>Balance Remaining</label>
            <p>{currency(selectedPayment?.InvoiceBalance)}</p>
          </article>
          <article>
            <label>Gateway</label>
            <p>{selectedPayment?.GatewayProvider || 'Manual'}</p>
          </article>
          <article className="full-span">
            <label>Invoice Status</label>
            <p>{getInvoiceInfo(selectedPayment?.InvoiceID)?.ComputedStatus || selectedPayment?.InvoiceStatus || 'Pending'}</p>
          </article>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(successPopup)}
        title={successPopup?.title || 'Success'}
        onClose={() => setSuccessPopup(null)}
      >
        <div className="reminder-success-popup">
          <div className="success-popup-icon-wrap">
            <FaCheckCircle className="success-popup-icon" />
          </div>
          <h3 className="success-popup-title">{successPopup?.title || 'Reminder Sent!'}</h3>
          <p className="success-popup-message">{successPopup?.message}</p>

          <div className="success-popup-details">
            {successPopup?.studentName && (
              <div className="success-detail-row">
                <span>Student:</span>
                <strong>{successPopup.studentName}</strong>
              </div>
            )}
            {successPopup?.invoiceId && (
              <div className="success-detail-row">
                <span>Invoice:</span>
                <strong>#{successPopup.invoiceId}</strong>
              </div>
            )}
            {successPopup?.balance !== undefined && successPopup?.balance !== null && (
              <div className="success-detail-row">
                <span>Balance Due:</span>
                <strong>{currency(successPopup.balance)}</strong>
              </div>
            )}
            {successPopup?.contactNumber && successPopup?.contactNumber !== 'N/A' && (
              <div className="success-detail-row">
                <span>Contact / WhatsApp:</span>
                <strong>{successPopup.contactNumber}</strong>
              </div>
            )}
          </div>

          <div className="success-popup-actions">
            <button
              type="button"
              className="btn btn-primary success-popup-btn"
              onClick={() => setSuccessPopup(null)}
            >
              OK
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default PaymentsPage;
